<template>
	<div ref="observerTarget" :data-block-name="title">
		<slot></slot>
	</div>
</template>

<script>
import analytics from '@/modules/analytics'
import { stepEvents } from '@/composables/useOnboardingStepEvents'

export default {
	name: 'BlockVisibilityObserver',
	emits: ['hide', 'show'],

	props: {
		// Block name (e.g. "LANDING_BENEFITS", "PAYWALL_CUSTOMER_REVIEWS_BLOCK").
		// Mapped to OnboardingBlockView.block_name in med-analytics schema.
		// eslint-disable-next-line vue/require-default-prop
		title: {
			type: String,
		},

		rootMargin: {
			type: String,
			default: '0px',
		},

		threshold: {
			type: [Number, Array],
			default: 0,
		},
	},

	data() {
		return {
			isAnalyticsEventSent: false,
		}
	},

	mounted() {
		// Two observers: keep show/hide emit semantics tied to the caller-supplied `threshold`
		// (PayLayout / PaywallPlanPicker rely on this), and run analytics on a separate observer
		// with a wider threshold set + a true visible-fraction check.  That's necessary because
		// `intersectionRatio` caps at `viewport / target` for targets taller than the viewport,
		// so `threshold: 1` never fires for sections taller than the viewport.
		this._emitObserver = new IntersectionObserver((entries) => entries.forEach((entry) => this.onEmit(entry)), {
			rootMargin: this.rootMargin,
			threshold: this.threshold,
		})
		this._emitObserver.observe(this.$refs.observerTarget)

		if (this.title) {
			this._analyticsObserver = new IntersectionObserver(
				(entries) => entries.forEach((entry) => this.onAnalytics(entry)),
				{ rootMargin: this.rootMargin, threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] },
			)
			this._analyticsObserver.observe(this.$refs.observerTarget)
		}
	},

	beforeUnmount() {
		this._emitObserver?.disconnect()
		this._emitObserver = null
		this._analyticsObserver?.disconnect()
		this._analyticsObserver = null
	},

	methods: {
		onEmit(entry) {
			if (entry.isIntersecting) {
				this.$emit('show', entry)
			} else {
				this.$emit('hide', entry)
			}
		},

		onAnalytics(entry) {
			if (this.isAnalyticsEventSent) return
			// Hybrid visibility check that works for BOTH short and tall targets, and
			// survives fast scroll (per AL-12446 — old Jet impl skipped upper blocks when
			// IntersectionObserver throttled callbacks).
			//
			//   1. ≥50% of target visible OR viewport ≥50% filled by target → "seen" right now.
			//      `intersectionRatio` alone is unreliable for tall targets (caps at
			//      viewportH/targetH ≈ 0.45 for a 2000px section in 900px viewport).
			//   2. target.bottom ≤ 0 → user has already scrolled past the block.  IO sometimes
			//      delivers only the final state on rapid scroll; this branch catches it.
			//      Guard: only after we've seen any prior intersection, so blocks below the
			//      fold that were never scrolled to don't accidentally fire.
			const rect = entry.boundingClientRect
			const visibleH = entry.intersectionRect.height
			const targetH = rect.height || 1
			const viewportH = entry.rootBounds?.height || window.innerHeight || 1
			const fraction = Math.max(visibleH / targetH, visibleH / viewportH)

			if (entry.isIntersecting || fraction > 0) {
				this._hasBeenIntersecting = true
			}

			const isHalfVisible = fraction >= 0.5
			const isScrolledPast = this._hasBeenIntersecting && rect.bottom <= 0
			if (!isHalfVisible && !isScrolledPast) return

			// Step context (onboarding_step_id / onboarding_title) comes from the
			// module-level `stepEvents` singleton in useOnboardingStepEvents — kept in
			// sync by jetStepShow/Done across Landing / Onboarding / Paywall views.
			analytics.track('OnboardingBlockView', {
				...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
				...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
				block_name: this.title,
			})
			this.isAnalyticsEventSent = true
			this._analyticsObserver?.disconnect()
			this._analyticsObserver = null
		},
	},
}
</script>
